import { Group, Stack, Title } from "@mantine/core";
import { IconUser } from "@tabler/icons-react";
import type { Task } from "@medplum/fhirtypes";
import { Fragment } from "react";
import { HoverCopyKeyValue } from "@/components/shared/detail-views/HoverCopyKeyValue";
import { getTaskTypeDisplay } from "@benchmark/core/resolvers";
import styles from "./TaskModalHeader.module.css";

/**
 * Props for the task modal header.
 * @agentic connections
 */
export type TaskModalHeaderProps = {
  actionLabel: "Complete" | "Log Progress";
  task: Task;
  patientName?: string;
  coverageLabel?: string;
  insuranceMemberId?: string;
  referringPracticeLabel?: string;
};

/**
 * Props for reusable task patient context row.
 * @agentic connections
 */
export type TaskPatientContextProps = {
  patientName?: string;
  coverageLabel?: string;
  insuranceMemberId?: string;
  referringPracticeLabel?: string;
  className?: string;
  /**
   * Additive, opt-in dark treatment for consumers rendering this row on a
   * dark surface (e.g. `TaskHistoryEntryCard`, inside the dark Task History
   * drawer). Defaults to `"light"` so existing light-mode consumers
   * (`TaskModalHeader` → `TaskCard`, `ActivityProgressWizard`,
   * `ActivityCompletionWizard`, `TasksPage`) are unaffected.
   */
  colorScheme?: "light" | "dark";
};

/**
 * Resolves the user-facing task label for modal title text.
 * @agentic connections
 */
export const resolveTaskLabelForModal = (task: Task): string => {
  const codingDisplay = task.code?.coding?.[0]?.display?.trim();
  if (codingDisplay) {
    return codingDisplay;
  }

  const description = task.description?.trim();
  if (description) {
    return description;
  }

  return getTaskTypeDisplay(task);
};

/**
 * Resolves full modal title text for a task action.
 * @agentic connections
 */
export const resolveTaskModalTitle = (
  actionLabel: TaskModalHeaderProps["actionLabel"],
  task: Task,
): string => `${actionLabel}: ${resolveTaskLabelForModal(task)}`;

/**
 * Converts coverage display into modal compact label (plan first; insurer fallback).
 * @agentic connections
 */
export const resolveCoverageLabelForModal = (
  coverageLabel?: string,
): string | undefined => {
  const trimmed = coverageLabel?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed;
};

/**
 * Shared patient context row used in task surfaces (modals and cards).
 * @agentic connections
 */
export const TaskPatientContext = ({
  patientName,
  coverageLabel,
  insuranceMemberId,
  referringPracticeLabel,
  className,
  colorScheme = "light",
}: TaskPatientContextProps) => {
  const isDark = colorScheme === "dark";
  const coverageToken = resolveCoverageLabelForModal(coverageLabel);
  const normalizedInsuranceMemberId = insuranceMemberId?.trim() ?? "";
  const tokenClassName = isDark ? styles.contextTokenDark : styles.contextToken;

  const contextTokens = [
    patientName?.trim() ? (
      <span className={tokenClassName}>{patientName.trim()}</span>
    ) : null,
    coverageToken ? (
      normalizedInsuranceMemberId ? (
        // NOTE: `HoverCopyKeyValue` has no dark-mode treatment of its own.
        // `TaskHistoryEntryCard` (the only current dark, colorScheme="dark"
        // consumer of this component) never passes `insuranceMemberId`, so
        // this branch is unreachable in dark mode today. If a future
        // consumer starts passing it through while dark, `HoverCopyKeyValue`
        // will need the same treatment.
        <HoverCopyKeyValue
          label={coverageToken}
          value={normalizedInsuranceMemberId}
          valueDisplayPrefix="#"
          copyHint="Copy Insurance ID"
          align="center"
        />
      ) : (
        <span className={tokenClassName}>{coverageToken}</span>
      )
    ) : null,
    referringPracticeLabel?.trim() ? (
      <span className={tokenClassName}>{referringPracticeLabel.trim()}</span>
    ) : null,
  ].filter((value) => value !== null);

  if (contextTokens.length === 0) {
    return null;
  }

  return (
    <Group
      gap={8}
      wrap="nowrap"
      align="center"
      className={
        className ? `${styles.contextRow} ${className}` : styles.contextRow
      }
    >
      <IconUser
        size={14}
        className={isDark ? styles.contextIconDark : styles.contextIcon}
      />
      {contextTokens.map((token, index) => (
        <Fragment key={`context-token-${index}`}>
          {index > 0 && (
            <span
              className={isDark ? styles.separatorDark : styles.separator}
              aria-hidden="true"
            >
              ·
            </span>
          )}
          {token}
        </Fragment>
      ))}
    </Group>
  );
};

/**
 * Shared header renderer used in task progress/completion modals.
 * @agentic connections
 */
export const TaskModalHeader = ({
  actionLabel,
  task,
  patientName,
  coverageLabel,
  insuranceMemberId,
  referringPracticeLabel,
}: TaskModalHeaderProps) => {
  return (
    <Stack gap={4} className={styles.header}>
      <Title order={2} size="h3" className={styles.title}>
        {resolveTaskModalTitle(actionLabel, task)}
      </Title>
      <TaskPatientContext
        patientName={patientName}
        coverageLabel={coverageLabel}
        insuranceMemberId={insuranceMemberId}
        referringPracticeLabel={referringPracticeLabel}
      />
    </Stack>
  );
};
